/**
 * InputController — the ONE place that turns key events into paramBus writes.
 *
 * Activation model (uniform for all 26 effects): a plain press is momentary —
 * the effect is on (`fx.<e>` = 1) while the key is held and off on release.
 * Shift+key toggles a *latch*: the first Shift+press pins the effect on so it
 * survives keyup; a second Shift+press releases it. A latched effect ignores
 * plain taps (it stays on), so only Shift+key, `clearAll()`, or a reset turns
 * it off. There is a single handleKeyDown/handleKeyUp path; the only per-effect
 * data is the key→effect map in effectConfig.
 *
 * The Tab key randomizes: a plain hold turns on a few random effects (cleared on
 * release); Shift+Tab latches a random set on. Auto-pilot (see AutoPilot) drives
 * the same random primitives on the beat via rollAuto()/stopAuto(). (Reroll — a
 * fresh base visual + palette — lives on `~`, wired in main.ts.)
 *
 * It writes to the bus and never reads keyboard events beyond what's handed to
 * it, and it never touches the renderer. Effects observe the bus; they never
 * see a KeyboardEvent.
 */

import { ParamBus } from '../state/paramBus';
import { EffectDef, effectConfig, effectByKey, effectParam, latchParam } from '../effects/effectConfig';
import { VisualDef, visualByKey, visualConfig, VISUAL_PARAM } from '../effects/visualConfig';

/** The randomize key — normalized identity of the Tab key. */
export const RANDOMIZE_KEY = 'Tab';

/** How many effects a randomize/auto-pilot roll turns on (inclusive range). */
export const RANDOM_MIN = 2;
export const RANDOM_MAX = 4;

export interface KeyModifiers {
  shift?: boolean;
}

export class InputController {
  /** Effect ids currently latched on (persist through keyup). */
  private readonly latched = new Set<string>();
  /** Effects turned on by the current plain-`~` hold, cleared on release. */
  private randomHeld: string[] = [];
  /** Effects turned on by the current auto-pilot beat, replaced each beat. */
  private autoHeld: string[] = [];

  constructor(
    private readonly bus: ParamBus,
    private readonly config: ReadonlyMap<string, EffectDef> = effectByKey,
    private readonly visuals: ReadonlyMap<string, VisualDef> = visualByKey,
    private readonly random: () => number = Math.random,
  ) {}

  handleKeyDown(key: string, mods: KeyModifiers = {}): void {
    if (key === RANDOMIZE_KEY) {
      if (mods.shift) this.randomizeLatched();
      else this.randomizeMomentary();
      return;
    }
    const def = this.config.get(key);
    if (def) {
      this.applyEffect(def, mods);
      return;
    }
    // Number row: 1–7 pick the base visual (exclusive, Shift-agnostic). 8–0 pick
    // the colour-palette type — owned by PaletteController, ignored here.
    const visual = this.visuals.get(key);
    if (visual) this.bus.set(VISUAL_PARAM, visual.index);
  }

  handleKeyUp(key: string, _durationMs: number): void {
    if (key === RANDOMIZE_KEY) {
      this.releaseRandom();
      return;
    }
    const def = this.config.get(key);
    if (!def) return; // visual/palette selectors are exclusive — no key-up behaviour
    // Momentary release, unless the effect is latched on.
    if (!this.latched.has(def.effect)) {
      this.bus.set(effectParam(def.effect), 0);
    }
  }

  private applyEffect(def: EffectDef, mods: KeyModifiers): void {
    if (mods.shift) {
      // Shift toggles the latch: first Shift+key pins on, a second releases it.
      if (this.latched.has(def.effect)) this.unlatch(def.effect);
      else this.latch(def.effect);
      return;
    }
    // Plain press: momentary on. (A latched effect is already on; this re-asserts
    // 1, a no-op on the bus, and keyup leaves it on because it's latched.)
    this.bus.set(effectParam(def.effect), 1);
  }

  private latch(effect: string): void {
    this.latched.add(effect);
    this.bus.set(effectParam(effect), 1);
    this.bus.set(latchParam(effect), 1);
  }

  private unlatch(effect: string): void {
    this.latched.delete(effect);
    this.bus.set(effectParam(effect), 0);
    this.bus.set(latchParam(effect), 0);
  }

  /** Turn `~` on: a random handful of effects, active until `~` releases. */
  private randomizeMomentary(): void {
    this.randomHeld = this.pickRandom();
    this.activate(this.randomHeld);
  }

  private releaseRandom(): void {
    this.deactivate(this.randomHeld);
    this.randomHeld = [];
  }

  /** Shift+Tab: latch a random set on (each cleared like any latched effect). */
  private randomizeLatched(): void {
    for (const effect of this.pickRandom()) {
      if (!this.latched.has(effect)) this.latch(effect);
    }
  }

  /** Auto-pilot beat: swap the previous auto set for a fresh random one. */
  rollAuto(): void {
    this.deactivate(this.autoHeld);
    this.autoHeld = this.pickRandom();
    this.activate(this.autoHeld);
  }

  /** Auto-pilot off: clear whatever the last beat turned on. */
  stopAuto(): void {
    this.deactivate(this.autoHeld);
    this.autoHeld = [];
  }

  /** Tab: reroll the base visual; the palette is rerolled by PaletteController. */
  rerollLook(): void {
    this.bus.set(VISUAL_PARAM, this.randomIndex(visualConfig.length));
  }

  /** Step the base visual by `dir` (±1), wrapping — the swipe-left/right gesture. */
  stepVisual(dir: number): void {
    const len = visualConfig.length;
    const cur = Math.round(this.bus.get(VISUAL_PARAM, 0));
    this.bus.set(VISUAL_PARAM, (((cur + dir) % len) + len) % len);
  }

  /** Backspace / panic: turn every effect (momentary + latched) off. */
  clearAll(): void {
    for (const def of effectConfig) {
      this.bus.set(effectParam(def.effect), 0);
      this.bus.set(latchParam(def.effect), 0);
    }
    this.latched.clear();
    this.randomHeld = [];
    this.autoHeld = [];
  }

  private activate(effects: readonly string[]): void {
    for (const effect of effects) this.bus.set(effectParam(effect), 1);
  }

  private deactivate(effects: readonly string[]): void {
    for (const effect of effects) {
      if (!this.latched.has(effect)) this.bus.set(effectParam(effect), 0);
    }
  }

  /** Pick RANDOM_MIN..RANDOM_MAX distinct effect ids (excluding full-frame
   *  effects like Blackout/Whiteout, which would just blank the screen). */
  private pickRandom(): string[] {
    const pool = effectConfig.filter((d) => d.randomizable !== false).map((d) => d.effect);
    // Fisher–Yates partial shuffle for the first `count` slots.
    const span = RANDOM_MAX - RANDOM_MIN + 1;
    const count = Math.min(pool.length, RANDOM_MIN + Math.floor(this.random() * span));
    for (let i = 0; i < count; i++) {
      const j = i + Math.floor(this.random() * (pool.length - i));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count);
  }

  private randomIndex(length: number): number {
    return Math.floor(this.random() * length);
  }
}
